import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuthStore } from "../store/auth";
import type { AuthResponse, User } from "../types/models";

// detail из тела ошибки бэка (FastAPI {detail}) — порт reject(j.detail) из legacy api().
const detail = (error: unknown, fallback = "Ошибка"): string => {
  if (error && typeof error === "object" && "detail" in error) {
    const d = (error as { detail?: unknown }).detail;
    if (typeof d === "string") return d;
  }
  return fallback;
};

/** Восстановление сессии: GET /api/me при наличии токена (порт startup: enterApp(await api("/api/me"))). */
export function useMe() {
  const token = useAuthStore((s) => s.token);
  const hasUser = useAuthStore((s) => s.user !== null);
  return useQuery({
    queryKey: ["me"],
    enabled: !!token && !hasUser,
    queryFn: async (): Promise<User> => {
      const { data, error } = await api.GET("/api/me");
      if (error || !data) throw new Error(detail(error));
      return data as User;
    },
  });
}

// AuthReq из схемы: username/password/email (email с дефолтом "" — для входа шлём пустой).
interface Credentials {
  username: string;
  password: string;
  email: string;
}

function useAuthMutation(path: "/api/auth/login" | "/api/auth/register") {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (creds: Credentials): Promise<AuthResponse> => {
      const { data, error } = await api.POST(path, { body: creds });
      if (error || !data) throw new Error(detail(error, "Ошибка авторизации"));
      return data as AuthResponse;
    },
    onSuccess: (res) => setSession(res.token, res.user),
  });
}

export const useLogin = () => useAuthMutation("/api/auth/login");
export const useRegister = () => useAuthMutation("/api/auth/register");

/** Выход: POST /api/auth/logout, затем сброс сессии (порт doLogout без location.reload — React перерисует). */
export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  return useMutation({
    mutationFn: async () => {
      await api.POST("/api/auth/logout");
    },
    onSettled: () => clearSession(),
  });
}
